import { useMutation as defaultUseMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { ApolloInjection, FeatureEnrollment } from '@zapier/upsells/src/types';

type Props = {
  accountId: number | null | undefined;
  featureEnrollmentList: (FeatureEnrollment | null)[] | null | undefined;
  onSetAutoUpgradeFailure?: () => void;
  onSetAutoUpgradeSuccess?: () => void;
  upgradePlanId: string | null | undefined;
} & ApolloInjection;

export const setAutoUpgradeMutation = gql`
  mutation upsells_setAutoUpgradeMutation($input: AutoTaskTierUpdateInput!) {
    setAutoUpgrade(input: $input) {
      id
      accountId
      featureName
      isEnrolled
    }
  }
`;

export const useAutoUpgradeFeatureEnrollment = ({
  accountId,
  featureEnrollmentList,
  useMutation,
  onSetAutoUpgradeFailure,
  onSetAutoUpgradeSuccess,
  upgradePlanId,
}: Props) => {
  const _useMutation = useMutation || defaultUseMutation;
  const autoUpgradeFeature = featureEnrollmentList?.find(
    feature => feature?.featureName === 'AUTO_UPGRADE'
  );

  const [
    _setAutoUpgrade,
    { error: mutationError, loading: isLoadingMutation },
  ] = _useMutation(setAutoUpgradeMutation, {
    onCompleted: onSetAutoUpgradeSuccess,
    onError: onSetAutoUpgradeFailure,
  });

  const setAutoUpgrade = (isEnrolled: boolean) => {
    _setAutoUpgrade({
      variables: {
        input: {
          accountId,
          id: autoUpgradeFeature?.id,
          isEnrolled,
          upgradePlanId,
        },
      },
    });
  };

  const isAutoUpgradeEnabled = Boolean(autoUpgradeFeature?.isEnrolled);

  return {
    error: mutationError?.message,
    isAutoUpgradeEnabled,
    isEligibleForAutoUpgrade: !!autoUpgradeFeature && !isAutoUpgradeEnabled,
    isLoading: isLoadingMutation,
    setAutoUpgrade,
  };
};
